/**
 * Target organisation of a rollout card (PLAN-20260930 slice 5, challenge
 * 2026-10-10-p20260930-s05-cross-org-rollout-admin, condition 7).
 *
 * A card reads and writes the signed-in user's active organisation through the
 * organisation's own routes. When a platform administrator picks another
 * organisation, the card uses the staff routes, which take the target from the
 * path only. The staff routes go through infra-service's one staff entry,
 * /v1/infra/staff (agion/Agion#3154), which admits only a verified platform
 * administrator and forwards to governance with that person's own token. The
 * server decides every refusal; this module only chooses the route.
 */
import { apiClient, governanceApiClient } from '@/core/infra_api_client'

export interface RolloutTarget {
  /** The organisation whose rollout the card shows and changes. */
  organizationId: string
  /** The signed-in user's active organisation. */
  activeOrganizationId: string
}

/** The rollout cards the staff route serves. */
export type StaffRolloutCard = 'ui-variant' | 'mission-stack' | 'settings' | 'managed-chat-address'

export function isCrossOrganizationTarget(target: RolloutTarget | undefined): target is RolloutTarget {
  return target !== undefined && target.organizationId !== target.activeOrganizationId
}

/**
 * Path of a staff rollout route, relative to the infra API client (/v1/infra).
 * The organisation id travels in the path only, never in a header or the body.
 */
export function staffRolloutPath(card: StaffRolloutCard, organizationId: string, suffix?: 'history'): string {
  const base = `staff/rollout/${card}/organizations/${encodeURIComponent(organizationId)}`
  return suffix ? `${base}/${suffix}` : base
}

/**
 * The API client for a card's routes: the infra staff entry for another
 * organisation, governance's organisation routes for the active one.
 */
export function rolloutApiClient(target: RolloutTarget | undefined): typeof apiClient | typeof governanceApiClient {
  return isCrossOrganizationTarget(target) ? apiClient : governanceApiClient
}
